import * as ARS from "../../ars_ldm";
import { CaseWhenCondition } from "../../contexts";
import {
  isSubClauseWithCompoundExpression,
  isSubClauseWithCondition,
  isSubClauseWithReference,
  isWhereClauseWithCondition,
} from "../../typeGuards";
import { ArsListingColumn } from "../interface";

/**
 * Get variables and datasets used in the given columns.
 *
 * @param columns - Listing columns to extract variables and datasets from
 * @returns Sets of unique dataset names and dataset.variable names
 */
export const variablesFromColumns = <T>(
  columns: ArsListingColumn<T>[],
): {
  datasets: Set<string>;
  variables: Set<string>;
} => {
  const datasets = new Set<string>();
  const variables = new Set<string>();

  columns.forEach((column) => {
    column.variables.forEach((variable) => {
      datasets.add(variable.dataset.toUpperCase());
      variables.add(`${variable.dataset}.${variable.variable}`.toUpperCase());
    });

    if (column.caseWhenCondition) {
      variablesFromCaseWhen([column.caseWhenCondition]).forEach((caseWhenVariable) => {
        variables.add(caseWhenVariable.toUpperCase());
        const [dataset] = caseWhenVariable.split(".");
        if (dataset) {
          datasets.add(dataset.toUpperCase());
        }
      });
    }
  });

  return { datasets, variables };
};

/**
 * Get datasets used in the given columns.
 *
 * @param columns - Listing columns to extract datasets from
 * @returns Set of unique dataset names used in the columns
 */
export const datasetsFromColumns = <T>(columns: ArsListingColumn<T>[]): Set<string> => {
  return variablesFromColumns(columns).datasets;
};

/**
 * Get variables used in case when conditions.
 *
 * @param caseWhenConditions - Case when conditions to inspect
 * @returns Set of unique variable names used in the case when conditions
 */
export const variablesFromCaseWhen = <T>(
  caseWhenConditions: CaseWhenCondition<T>[],
): Set<string> => {
  const variables = new Set<string>();

  caseWhenConditions.forEach((caseWhen) => {
    caseWhen.caseWhenItems.forEach((item) => {
      if (item.when) {
        variablesFromWhereClause(item.when).forEach((variable) => {
          variables.add(variable.toUpperCase());
        });
      }
    });
  });

  return variables;
};

/**
 * Get variables used in a given condition.
 *
 * @param condition - The condition to extract variables from
 * @returns Set of unique variable names used in the condition
 */
export const variablesFromCondition = (condition: ARS.WhereClauseCondition): Set<string> => {
  const variables = new Set<string>();
  if (condition.variable) {
    variables.add(`${condition.dataset ?? ""}.${condition.variable}`.toUpperCase());
  }
  return variables;
};

/**
 * Get variables used in a given compound expression.
 * Recursively traverses the expression tree to find all variables used in any conditions.
 *
 * @param compoundExpression - The compound expression to extract variables from
 * @returns Set of unique variable names used in the compound expression
 */
export const variablesFromCompoundExpression = (
  compoundExpression: ARS.WhereClauseCompoundExpression,
): Set<string> => {
  const variables = new Set<string>();
  compoundExpression.whereClauses?.forEach((sc) => {
    if (isSubClauseWithCondition(sc)) {
      variablesFromCondition(sc.condition).forEach((variable) => {
        variables.add(variable.toUpperCase());
      });
    } else if (isSubClauseWithCompoundExpression(sc)) {
      variablesFromCompoundExpression(sc.compoundExpression).forEach((variable) => {
        variables.add(variable.toUpperCase());
      });
    } else if (isSubClauseWithReference(sc)) {
      variablesFromReference(sc.subClauseId).forEach((variable) => {
        variables.add(variable.toUpperCase());
      });
    }
  });
  return variables;
};

/**
 * Get variables used in a given where clause.
 *
 * @param whereClause - The where clause to extract variables from
 * @returns Set of unique variable names used in the where clause
 */
export const variablesFromWhereClause = (whereClause: ARS.WhereClause): Set<string> => {
  if (isWhereClauseWithCondition(whereClause)) {
    return variablesFromCondition(whereClause.condition);
  } else {
    return variablesFromCompoundExpression(whereClause.compoundExpression);
  }
};

/**
 * Get variables used in a given reference (analysis set or data subset).
 *
 * @param subClauseId - The ID of the sub-clause to extract variables from
 * @returns Set of unique variable names used in the reference
 */
export const variablesFromReference = (subClauseId: string): Set<string> => {
  return new Set<string>([`ARS_REF: ${subClauseId.toUpperCase()}`]);
};
